import { useLayoutEffect, useRef } from 'react';

import { NullableNumber } from '../../lib/util/types';

import { useMeasure } from '../hooks/useMeasure';

import { LINE_CHART_WIDTH } from './constants';

import styles from './SparklineCanvas.module.scss';

const REFERENCE_LINE_COLOR = '#c6c8da';
const REFERENCE_LINE_DASH = [ 3, 3 ];
const REFERENCE_LINE_WIDTH = 0.5;
const GRADIENT_TOP_ALPHA = '40';
const GRADIENT_BOTTOM_ALPHA = '00';
const DOMAIN_PADDING_RATIO = 0.001;

export type PaddingRatio = {
  left: number;
  right: number;
};

type DrawConfig = {
  values: number[];
  referenceValue: NullableNumber;
  color: string;
  width: number;
  height: number;
  maxPoints: number;
  paddingRatio: PaddingRatio;
  strokeWidth: number;
  withGradient: boolean;
};

type Props = Omit<DrawConfig, 'width' | 'height'> & {
  designHeight: number;
};

const getYDomain = (values: number[], referenceValue: NullableNumber) => {
  let min = values[0];
  let max = values[0];

  values.forEach((value) => {
    if (value < min) min = value;
    if (value > max) max = value;
  });

  if (referenceValue != null) {
    min = Math.min(min, referenceValue);
    max = Math.max(max, referenceValue);
  }

  return {
    min: min - (DOMAIN_PADDING_RATIO * min),
    max: max + (DOMAIN_PADDING_RATIO * max),
  };
};

/* eslint-disable no-param-reassign -- drawing to a canvas context means setting its properties */
const drawSparkline = (ctx: CanvasRenderingContext2D, config: DrawConfig) => {
  const {
    values, referenceValue, color, width, height, maxPoints, paddingRatio, strokeWidth, withGradient,
  } = config;

  const paddingLeft = width * paddingRatio.left;
  const chartWidth = width - paddingLeft - (width * paddingRatio.right);
  if (chartWidth <= 0 || height <= 0 || values.length === 0) return;

  const { min, max } = getYDomain(values, referenceValue);
  const range = max - min || 1;
  const totalPoints = Math.max(maxPoints, values.length);
  const stepX = totalPoints > 1 ? chartWidth / (totalPoints - 1) : 0;

  const toX = (index: number) => paddingLeft + (index * stepX);
  const toY = (value: number) => height - (((value - min) / range) * height);

  const traceSeries = () => {
    ctx.beginPath();
    values.forEach((value, index) => {
      const x = toX(index);
      const y = toY(value);
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
  };

  if (withGradient) {
    traceSeries();
    ctx.lineTo(toX(values.length - 1), height);
    ctx.lineTo(toX(0), height);
    ctx.closePath();

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, `${color}${GRADIENT_TOP_ALPHA}`);
    gradient.addColorStop(1, `${color}${GRADIENT_BOTTOM_ALPHA}`);
    ctx.fillStyle = gradient;
    ctx.fill();
  }

  if (referenceValue != null) {
    ctx.save();
    ctx.strokeStyle = REFERENCE_LINE_COLOR;
    ctx.lineWidth = REFERENCE_LINE_WIDTH;
    ctx.setLineDash(REFERENCE_LINE_DASH);
    const referenceY = toY(referenceValue);
    ctx.beginPath();
    ctx.moveTo(paddingLeft, referenceY);
    ctx.lineTo(paddingLeft + chartWidth, referenceY);
    ctx.stroke();
    ctx.restore();
  }

  traceSeries();
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
};
/* eslint-enable no-param-reassign */

const SparklineCanvas = (props: Props) => {
  const {
    values, referenceValue, color, maxPoints, paddingRatio, strokeWidth, withGradient, designHeight,
  } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { ref: containerRef, width, height } = useMeasure<HTMLDivElement>();

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0 || height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    drawSparkline(ctx, {
      values, referenceValue, color, width, height, maxPoints, paddingRatio, strokeWidth, withGradient,
    });
  }, [ values, referenceValue, color, width, height, maxPoints, paddingRatio, strokeWidth, withGradient ]);

  return (
    <div ref={containerRef} className={styles.container} style={{ aspectRatio: `${LINE_CHART_WIDTH} / ${designHeight}` }}>
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

export default SparklineCanvas;
